import React, { FunctionComponent } from 'react'
import Markdown, { MarkdownToJSX } from 'markdown-to-jsx'
import SyntaxHighlighter from 'react-syntax-highlighter'
import { vs2015 } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import icon from './images/icon.png'
import * as styles from './styles.module.css'

type ChildrenComponentsProps = {
  children: React.ReactNode,
}

const CodeComponent: React.FC<ChildrenComponentsProps> = ({ children }) => {
  let style = {...vs2015};
  style.hljs.background = null
  return (
    <div className={`${styles.highlight}`}>
      <SyntaxHighlighter language="javascript" style={style} className="dark:bg-white-bg-10 bg-body-bg1-dm max-w-full">
        {children.props && children.props.children ? children.props.children : ''}
      </SyntaxHighlighter>
    </div>
  )
}

export const H1Component: FunctionComponent<Props> = ({ children }) => {
  return <h1 className="font-ilisarniq mt-10 md:mt-20 md:mb-14 mb-5 text-32 dark:text-text-dm ">{children}</h1>
}

export const H2Component: FunctionComponent<Props> = ({ children }) => {
  return <h2 className="font-bold text-3.5xl tracking-tightest leading-big md:leading-big font-ilisarniq font-bold md:text-5xl dark:text-gray-darker text-text dark:text-text-dm md:mt-18 mt-8 md:mb-7 mb-5">{children}</h2>
}

export const H3Component: FunctionComponent<Props> = ({ children }) => {
  return <h3 className="font-ilisarniq md:mt-15 mt-7 md:mb-7 mb-5 text-22 text-text tracking-tightest dark:text-text-dm">{children}</h3>
}

export const H4Component: FunctionComponent<Props> = ({ children }) => {
  return <h4 className="font-ilisarniq md:mt-15 mt-7 md:mb-7 mb-5 text-text tracking-tightest dark:text-text-dm italic">{children}</h4>
}

export const H5Component: FunctionComponent<Props> = ({ children }) => {
  return <h5 className="font-ilisarniq md:mt-15 mt-7 md:mb-7 mb-5 text-text tracking-tightest dark:text-text-dm">{children}</h5>
}

export const H6Component: FunctionComponent<Props> = ({ children }) => {
  return <h6 className="font-ilisarniq md:mt-15 mt-7 md:mb-7 mb-5 text-text tracking-tightest dark:text-text-dm">{children}</h6>
}

export const PComponent: FunctionComponent<Props> = ({ children }) => {
  return <p className={'mb-5 mt-7 font-light text-lg text-black-lighter text-opacity-70 dark:text-opacity-70 dark:text-text-dm'}>{children}</p>
}

export const EmComponent: React.FC<ChildrenComponentsProps> = ({ children }) => (
  <em className="font-inter font-light leading-normal text-lg my-7.5 md:px-0 dark:text-text-70-dm">
    {children}
  </em>
)

export const StrongComponent: React.FC<ChildrenComponentsProps> = ({ children, className }) => (
  <strong className={`${className} font-inter font-bold text-black-lighter dark:text-text-dm leading-normal text-lg my-7.5 md:px-0 dark:text-text-80-dm`}>
    {children}
  </strong>
)

export const BlockquoteComponent: React.FC<ChildrenComponentsProps> = ({ children }) => (
  <blockquote className={`${styles.blockquote} italic my-15`}>
    <img src={icon} alt="icon" />
    {children}
  </blockquote>
)

export const UlComponent: React.FC<Props> = ({ children }) => <ul className="my-5">{children}</ul>

export const LiComponent: React.FC<Props> = ({ children }) => (
  <li className={`${styles.listStyle} px-0 relative leading-snug ml-7 mb-3 font-inter font-xl text-lg leading-semiBig text-black-lighter text-opacity-70 dark:text-opacity-70 dark:text-text-dm`}>
    {children}
  </li>
)

export const HrComponent: React.FC<Props> = ({ children }) => (
  <hr className="my-10 block" />
)

interface Props extends MarkdownToJSX.Options {
  children: string;
  externalComponents?: MarkdownToJSX.Overrides;
  className?: string;
}

export const MarkdownComponent: FunctionComponent<Props> = ({
  children,
  wrapper,
  externalComponents,
  className,
}) => {
  return (
    <Markdown
      className={className}
      options={{
        wrapper,
        forceBlock: true,
        overrides: {
          pre: CodeComponent,
          h1: H1Component,
          h2: H2Component,
          h3: H3Component,
          h4: H4Component,
          h5: H5Component,
          h6: H6Component,
          p: PComponent,
          em: EmComponent,
          strong: StrongComponent,
          blockquote: BlockquoteComponent,
          ul: UlComponent,
          li: LiComponent,
          hr: HrComponent,
          ...externalComponents,
        },
      }}
    >
      {children}
    </Markdown>
  )
}
